import { Stack } from 'react-bootstrap';
import { LoadingAnimation, Wizard } from '@odmonorepo/core-components';
import OwnerStep from './Cards/OwnerStep';
import AppReviewCard from './Cards/AppReview';
import DeclinedCard from './Cards/DeclinedCard';
import ReceivedCard from './Cards/ReceivedCard';
import BankConfirmationCard from './Cards/BankConfirmationCard';
import BankInfoCard from './Cards/BankInfoCard';
import { hasProvePrefillConsent, loanStep } from './helpers';
import { LoanAppStep } from 'merchant-api/derived';
import { useAnalytics } from 'use-analytics';
import React, { useEffect, useRef } from 'react';
import ConsentCard from './Cards/ConsentCard';
import { SetPassword } from '../Login/SetPassword/SetPassword';
import BusinessContactStep from './Cards/BusinessContactStep';
import BusinessDetailsStep from './Cards/BusinessDetailsStep';
import BusinessSelectStep from './Cards/BusinessSelectStep';
import ExpressEstimate from './Cards/ExpressEstimate';
import ProveStep from './Cards/ProveStep';
import { consumeIntakeBridge } from '../BasicInfo/intakeBridge';

const ExpressEstimateStep = () => {
  const { applicationData, stepLoading } = loanStep();
  if (stepLoading) return <div className="mx-auto"><LoadingAnimation /></div>;
  return <ExpressEstimate applicationData={applicationData} />;
};

// FINANCE_STEP no longer really exists (replaced by BUSINESS_CONTACT_STEP),
// but Marketing/Analytics is not ready for the switch yet, so NEW app types still use the FINANCE_STEP name
// The keys are pinned to LoanAppStep so this map stays in sync with the shared
// status -> step mapper (getLoanAppStep in merchant-api/derived).
export const steps: Record<LoanAppStep, React.ReactNode> = {
  EXPRESS_ESTIMATE: <ExpressEstimateStep />,
  PROVE_STEP: <ProveStep />,
  FINANCE_STEP: <BusinessContactStep />,
  BUSINESS_CONTACT_STEP: <BusinessContactStep />,
  BUSINESS_STEP: <BusinessDetailsStep />,
  BUSINESS_SELECT: <BusinessSelectStep />,
  OWNER_STEP: <OwnerStep />,
  CONSENT: <ConsentCard />,
  APP_REVIEW: <AppReviewCard />,
  BANK_CONFIRMATION: <BankConfirmationCard />,
  BANK_INFO: <BankInfoCard />,
  RECEIVED: <ReceivedCard />,
  DECLINED: <DeclinedCard />,
  PW_SET: <SetPassword />,
};

type StepKey = keyof typeof steps | `$${keyof typeof steps}`;

// $-prefixed steps are invisible pass-through effects: nextStep runs them in order, but they are
// excluded from the progress bar (SteppedRange filters them) and skipped by backward navigation
// (Wizard findStep). $BANK_CONFIRMATION self-skips to BANK_INFO when the plaid confirmation
// does not apply. Associate + submit no longer have steps: the single submit fired by
// APP_REVIEW (after CONSENT) associates the user server-side.
const renewalOrder: StepKey[] = ['BUSINESS_CONTACT_STEP', 'BUSINESS_STEP', 'OWNER_STEP', 'CONSENT', '$BANK_CONFIRMATION', 'BANK_INFO'];
const directOrder: StepKey[] = ['FINANCE_STEP', 'BUSINESS_STEP', 'OWNER_STEP', 'CONSENT', 'BANK_INFO'];
// PROVE_STEP (XF-10207) leads the Prove order: it is the first intake step for the M3 test bucket
// and is hidden from navigation once provePrefillConsent is set (getHiddenSteps), so a consented
// application that reloads into this order lands on OWNER_STEP (the mapper + entryStep below).
// BUSINESS_SELECT needs Prove matches, so it is hidden while there is no consent; with consent it
// still skips itself when Prove has no business matches for the applicant.
const directProvePrefillOrder: StepKey[] = ['PROVE_STEP', 'OWNER_STEP', 'BUSINESS_SELECT', 'FINANCE_STEP', 'BUSINESS_STEP', 'CONSENT', 'BANK_INFO'];
const strategicPartner: StepKey[] = ['BUSINESS_CONTACT_STEP', 'BUSINESS_STEP', 'OWNER_STEP', 'CONSENT', 'BANK_INFO'];

// The Prove order applies once consent is on the application (M1/M2 entry Prove, or a completed
// PROVE_STEP) and, before that, while the applicant is bucketed into PROVE_STEP. The control bucket
// gets the plain direct order — PROVE_STEP is not in it at all, so nothing needs to skip.
const getStepOrder = (applicationData, useProveOrder: boolean): StepKey[] => {
  if (applicationData?.type === 'RENEWAL') return renewalOrder;
  if (applicationData?.metadata?.channelCode === 'PARTNER_STRATEGIC') return strategicPartner;
  if (useProveOrder) return directProvePrefillOrder;
  return directOrder;
};

// Steps in the order the applicant will not see right now. The Wizard passes over them in both
// directions, so Back can never land on a step that immediately moves forward again, and leaves them
// out of the progress count and of isFirstStep() (which is what makes the owner step the first visible
// step again, with its old Back behaviour, once Prove consent is on the application).
//  - PROVE_STEP once consent is written: it only ever shows before consent.
//  - BUSINESS_SELECT while there is no consent: it needs Prove matches, so an applicant who skipped or
//    failed Prove (the manual path) would otherwise get a phantom step.
// With consent, BUSINESS_SELECT can still find no matches at runtime; it then uses the Wizard's
// direction-aware skipStep().
const getHiddenSteps = (hasConsent: boolean): string[] => (hasConsent ? ['PROVE_STEP'] : ['BUSINESS_SELECT']);

// Mirrors getStepOrder (same useProveOrder input, so an applicant bucketed into PROVE_STEP reports
// direct_prove_prefill from the first step instead of flipping labels when consent is written);
// MARKETING and ISO/REFERRAL get their own labels instead of direct_new.
const getFlowType = (applicationData, useProveOrder: boolean) => {
  if (applicationData?.type === 'RENEWAL') return 'renewal';
  if (applicationData?.metadata?.channelCode === 'PARTNER_STRATEGIC') return 'strategic_partner';
  if (useProveOrder) return 'direct_prove_prefill';
  if (applicationData?.metadata?.channelCode === 'MARKETING') return 'direct_marketing';
  if (applicationData?.metadata?.channelCode === 'PARTNER_ISO' || applicationData?.metadata?.channelCode === 'PARTNER_REFERRAL') return 'partner_direct';
  return 'direct_new';
};

export const LoanAppForm = () => {
  const { step, stepError, applicationId, applicationData, proveStepEligible } = loanStep();
  const analytics = useAnalytics();
  const hasTrackedBasicInfoAdvance = useRef(false);
  // Prove order once consent is on the application, or while the applicant is bucketed into PROVE_STEP.
  const hasConsent = hasProvePrefillConsent(applicationData);
  const useProveOrder = hasConsent || proveStepEligible;
  const flowType = getFlowType(applicationData, useProveOrder);
  const hiddenSteps = getHiddenSteps(hasConsent);

  useEffect(() => {
    // ThreatMetrix profiling moved to ApplicationLayout ($id.route.tsx), which covers every
    // authenticated /application destination (apply, renewal, offer, checkout), not just the wizard.
    let anonymousId = analytics.getUserId();
    applicationId && analytics.identify(anonymousId, { applicationId: applicationId, type: applicationData?.type, applicationUserId: applicationData?.userId || '', flowType });
  }, [applicationId]);

  const isDirectNewApp =
    applicationData?.type === 'NEW' &&
    (applicationData?.metadata?.channelCode === 'DIRECT' ||
      applicationData?.metadata?.channelCode === 'MARKETING' ||
      applicationData?.metadata?.channelCode === 'PARTNER_REFERRAL');

  // The mapper only picks the ENTRY step; the mounted Wizard owns navigation from then on
  // (AppReview advances the wizard once the decision lands). loanStep keeps recomputing while
  // its inputs refetch mid-flow - e.g. the submit orchestration writes a new sfOpportunityId,
  // which kicks off a fresh LDR fetch and flips stepLoading - so the entry step is captured
  // once and later loading flips must never unmount the Wizard: that would throw away the
  // current step and restart the flow mid-submit.
  const entryStep = useRef<LoanAppStep | undefined>(undefined);
  if (!entryStep.current && step) {
    entryStep.current = hasProvePrefillConsent(applicationData) && step === 'FINANCE_STEP' ? 'OWNER_STEP' : step;
  }

  const stepOrder = getStepOrder(applicationData, useProveOrder);
  // Analytics-only: offsets funnel step numbers for flows that start on BASIC_INFO.
  const hasBasicInfoPredecessor = flowType === 'direct_new' || flowType === 'direct_prove_prefill';

  // BASIC_INFO is a prior screen, so emit its StepChange into the first step once.
  useEffect(() => {
    if (hasTrackedBasicInfoAdvance.current || !entryStep.current) return;
    hasTrackedBasicInfoAdvance.current = true;
    // Always clear the one-shot marker once the entry step is known, so it can't leak to a later mount.
    const bridged = consumeIntakeBridge();
    // Skip reloads/resumes: only bridge right after a BasicInfo submit.
    if (!bridged || !hasBasicInfoPredecessor) return;
    // Skip resumed apps that re-enter mid-flow.
    const firstVisibleStep = stepOrder.find((s) => !String(s).includes('$') && !hiddenSteps.includes(String(s)));
    if (entryStep.current !== firstVisibleStep) return;

    const toStepName = String(entryStep.current);
    analytics.track('StepChange', {
      fromStep: 'BASIC_INFO',
      toStep: entryStep.current,
      step: 'BASIC_INFO',
      stepOrder: 1,
      fromStepName: 'BASIC_INFO',
      toStepName,
      transition: `BASIC_INFO->${toStepName}`,
      direction: 'forward',
      suppressBridgeTracking: true,
      allPlannedSteps: stepOrder,
      allPossibleSteps: Object.keys(steps),
      flowType,
    });
  }, [analytics, flowType, hasBasicInfoPredecessor, hasConsent, stepOrder, step]);

  if (!entryStep.current) {
    if (stepError) throw new Error('Error loading form');
    return (
      <Stack>
        <LoadingAnimation className="mx-auto mt-7" />
      </Stack>
    );
  }

  return (
    <Wizard
      initialStep={entryStep.current}
      steps={steps}
      stepOrder={stepOrder}
      hiddenSteps={hiddenSteps}
      progressOffset={isDirectNewApp ? 1 : 0}
      stepOrderOffset={hasBasicInfoPredecessor ? 1 : 0}
      emitStepAnalytics
      flowType={flowType}
      manualStepLoaded={['BUSINESS_SELECT']}
      onStepChange={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
    />
  );
};

export default LoanAppForm;
